import {getCookie} from '../../../../utils/custom-utils'
import {productUrlBuilder} from '../../../../../app/utils/url'

/**
 * Build common params for segment Events
 */
export const commonParams = async (segmentContext, email) => {
    let sessionId = await segmentContext?.getUsidWhenReady()
    if (!sessionId) {
        sessionId = getCookie('sid')
    }
    const params = {
        eventSource: segmentContext?.eventSource,
        email: email ? email : segmentContext?.customerEmail || '',
        siteVersion: segmentContext?.siteVersion,
        cid: segmentContext?.cookieId,
        sessionId: sessionId,
        pageType: segmentContext?.pageType,
        ...(segmentContext?.isMenuItem
            ? {
                  storeData: {
                      shopCountry: segmentContext?.shopCountry,
                      shopLanguage: segmentContext?.shopLanguage,
                      shopRegion: segmentContext?.shopRegion
                  }
              }
            : {
                  shopCountry: segmentContext?.shopCountry,
                  shopLanguage: segmentContext?.shopLanguage,
                  shopRegion: segmentContext?.shopRegion
              }),
        textOfButtonOrLink: segmentContext?.linkText
    }

    return params
}

/**
 * return lineItemsUUID from basket for the respective product
 */
export const lineItemsUUID = (basket, productId) => {
    if (basket?.productItems?.length > 0) {
        return basket.productItems.find((item) => item.productId === productId)?.itemId
    }
}

/**
 * Build common product params for segment Events
 */
export const productParams = async (
    segmentContext,
    product,
    basket,
    params = {},
    productDetail = {}
) => {
    const lineItemId = lineItemsUUID(basket, product.id)
    const prodObj = {
        productName: product?.productName || product?.name,
        productId: product?.productId || product?.id
    }
    let url = `${segmentContext?.appOrigin}${productUrlBuilder(prodObj)}`
    return {
        ...params,
        brand: product?.brand ?? productDetail?.brand,
        category:
            product?.c_category ??
            product?.representedProduct?.c_category ??
            productDetail?.c_category,
        currency: product?.currency,
        name: product?.name ?? product?.productName,
        price: product?.price,
        quantity: product?.stepQuantity || 1,
        sku: product?.id ?? product?.productId,
        url: url || window.location.href,
        imageurl: product?.imageGroups?.[0]?.images?.[0]?.disBaseLink,
        productId: lineItemId || (product?.id ?? product?.productId),
        globalName: product?.c_globalName || product?.productName
    }
}

/**
 * Appends UTM parameters from cookies to the provided params.
 * @param {Object} [params={}] - Existing parameters to extend.
 * @returns {Promise<Object>} Parameters including UTM data.
 */
export const utmParams = async (params = {}) => {
    return {
        ...params,
        utmSource: getCookie('utm_source'),
        utmMedium: getCookie('utm_medium'),
        utmCampaign: getCookie('utm_campaign'),
        utmTerm: getCookie('utm_term'),
        utmContent: getCookie('utm_content')
    }
}

// handling empty panelTitle
export const getPanelTitle = (currentElement, wrapper) => {
    const cleanText = (text) => text?.replace(/[\n\t]/g, '')?.trim() || ''
    let panelTitle = ''

    if (wrapper) {
        const parentSlide = currentElement.closest('.slick-slide')
        panelTitle = cleanText(parentSlide?.querySelector('.product-tile-body p')?.textContent)
    }

    if (!panelTitle) {
        const parent = currentElement.parentElement
        let headingEl =
            parent?.querySelector('.heading, .chakra-heading') ||
            currentElement.closest('.heading, .chakra-heading') ||
            currentElement
        panelTitle = cleanText(headingEl?.textContent)
    }

    if (!panelTitle) {
        panelTitle = cleanText(currentElement.querySelector('img')?.getAttribute('alt'))
    }

    return panelTitle
}

/**
 * Extracts metadata from the DOM related to a panel component (e.g., carousel or grid).
 * @param {Event} event - The event triggered from the panel element.
 * @param {Object} [shopbyseries={}] - Optional metadata like position and panel title.
 * @returns {Object} Panel metadata including layout, feed info, and position.
 */
export const getPanelDomData = (event, shopbyseries = {}) => {
    const currentElement = event?.currentTarget ?? event?.target
    const wrapper = currentElement.closest('.carousel-wrapper')
    const componentCount = currentElement
        ?.closest('[data-component-count]')
        ?.getAttribute('data-component-count')
    const position = componentCount?.split('-') || []
    const containerPosition = position?.[1] ? Number(position[1]) : ''
    let componentPositionInContainer = position?.[2] ? Number(position[2]) : ''
    if (position.length == 2 && componentPositionInContainer == '') {
        componentPositionInContainer = 0
    }
    let panelTitle = shopbyseries?.panelTitle || ''
    let {feedTitle, feedType} = feedContextprams(currentElement)

    if (!panelTitle) {
        panelTitle = getPanelTitle(currentElement, wrapper)
    }
    let containerRepresentation = wrapper
        ? 'Carousel'
        : currentElement?.closest('.multi-image-container-main-block')
        ? 'Grid'
        : 'single'
    let positionInContainer =
        shopbyseries?.positionInContainer ||
        Number(currentElement?.closest('[data-position]')?.dataset?.position) ||
        componentPositionInContainer

    if ((positionInContainer == null || positionInContainer === '') && wrapper) {
        let parentComponent = currentElement.closest('.slick-slide')
        positionInContainer = parentComponent?.getAttribute('[data-index]')
    }

    return {
        containerRepresentation,
        containerPosition,
        feedType,
        feedTitle,
        positionInContainer,
        title: document.title || '',
        panelTitle: panelTitle
    }
}

/**
 * Builds enriched panel tracking parameters, combining DOM data, context, and product info.
 * @param {Object} pannelDomData - Data extracted from the panel DOM.
 * @param {Object} segmentContext - The current segment tracking context.
 * @param {Object} product - Product object.
 * @param {Object} [params={}] - Additional params to merge.
 * @returns {Promise<Object>} Final panel parameters including store data and common fields.
 */
export const pannelparams = async (pannelDomData, segmentContext, product, event) => {
    const currentElement = event?.currentTarget ?? event?.target
    let storeData
    const extendContext = {...segmentContext, isMenuItem: true}
    const {productSalePrice, ...restPannelDomData} = pannelDomData
    const {storeData: commonStoreData, ...restCommonData} = await commonParams(extendContext)
    const pannelParams = {
        ...restPannelDomData,
        ...restCommonData
    }
    pannelParams.referrer = window?.location?.href
    pannelParams.title = document.title || ''
    const promotionalPrice = product?.productPromotions?.[0]?.promotionalPrice
    const salePrice = promotionalPrice ?? product?.price ?? null
    pannelParams.productSalePrice = salePrice

    if (product && Object.keys(product).length > 0) {
        pannelParams.panelTitle = product?.name ?? product?.productName
        pannelParams.productSalePrice = productSalePrice
        const productSpecificData = await productParams(segmentContext, product)
        storeData = {
            ...productSpecificData,
            ...commonStoreData,
            productName: product?.name ?? product?.productName,
            inventoryStatus: inventoryStatus(product)
        }
    } else {
        // Fallback when product data is not available
        storeData = {
            quantity: 1,
            url: window.location.href,
            ...commonStoreData
        }
    }
    pannelParams.locale = segmentContext?.localeId
    // Attach final storeData to productData
    pannelParams.storeData = storeData
    if (pannelParams.storeData && !pannelParams.storeData.salePrice) {
        const parentComponent = currentElement?.closest('.slick-slide')

        let rawPrice = ''
        if (parentComponent) {
            if (parentComponent.querySelector('.list-price')) {
                rawPrice =
                    parentComponent.querySelector('.current-price')?.textContent?.trim() || ''
            } else {
                rawPrice = parentComponent.querySelector('.sale-price')?.textContent?.trim() || ''
            }
        }

        if (rawPrice) {
            pannelParams.storeData.salePrice = parseFloat(rawPrice.replace(/[^0-9.-]+/g, ''))
        }
    }

    return pannelParams
}

/**
 * Extracts product list view metadata from the DOM and a search result object.
 * @param {Object} productSearchResult - Search result object containing product hits.
 * @returns {Object} Metadata including filters, sort order, taxonomy, and product list.
 */
export const getProductListViewedDomData = (
    productSearchResult,
    appOrigin,
    plpFeedType,
    currentElement
) => {
    // Extract filters
    const filters =
        typeof document !== 'undefined'
            ? Array.from(document.querySelectorAll('.selectedFilters li button')).map((button) => ({
                  filterType: button?.getAttribute('data-refinementname'),
                  filterValue: button?.getAttribute('data-refinementvalue')
              }))
            : []
    let pageTaxonomy = ''

    if (typeof document !== 'undefined') {
        const breadcrumbItems = Array.from(document.querySelectorAll('nav.sf-breadcrumb ol li'))
        const taxonomyArray = breadcrumbItems
            .map((item) => item.textContent.trim())
            .filter((text) => !!text)
        pageTaxonomy = taxonomyArray.length > 0 ? taxonomyArray.join('/') : ''
    }
    const sortOption = document.getElementById('page_sort')
    const selectedSortOption = sortOption?.options[sortOption.selectedIndex]?.text
    const productsArray = productSearchResult?.hits || productSearchResult
    const products = productsArray?.map((item, index) => {
        const prodObj = {
            productName: item?.productName || item?.name,
            productId: item?.productId || item?.id
        }
        let url = `${appOrigin}${productUrlBuilder(prodObj)}`
        return {
            productid: item?.productId || item?.id,
            sku: item?.productId || item?.id,
            name: item?.productName || item?.name,
            globalName: item?.c_globalName || item?.productName || item?.name,
            inventoryStatus: inventoryStatus(item),
            price: item?.price,
            salePrice: item?.productPromotions?.[0]?.promotionalPrice ?? item?.price ?? null,
            currency: item?.currency || 'USD',
            position: index,
            category: productSearchResult?.selectedRefinements?.cgid || item?.c_category || '',
            url: url,
            imageurl: item?.imageUrl || ''
        }
    })

    const pagePath = typeof window !== 'undefined' ? window.location.pathname : ''
    const fullPath = typeof window !== 'undefined' ? window.location.origin + pagePath : ''
    const referrerURL = typeof document !== 'undefined' ? document.referrer : ''

    let {feedTitle, feedType} = feedContextprams(currentElement)
    if (plpFeedType) {
        feedType = plpFeedType
    }
    return {
        filters,
        pageTaxonomy,
        selectedSortOption,
        products,
        fullPath,
        referrerURL,
        pagePath,
        feedTitle,
        feedType
    }
}

/**
 * Builds tracking parameters specific to a product view.
 * @param {Object} product - The product being viewed.
 * @returns {Object} Tracking data including image URL, inventory, and page metadata.
 */
export const productViewedParams = (product) => {
    const searchParams = new URLSearchParams(location.search)
    const querystring =
        searchParams?.size > 0 ? `pid=${product?.id}&${searchParams}` : `pid=${product?.id}`

    let imageUrl = ''

    if (product?.imageGroups?.length && Array.isArray(product.imageGroups)) {
        const img = product.imageGroups.find((g) => g.viewType === 'large')?.images?.[0]
        const fullUrl = img?.link || img?.disBaseLink // adjust `disBaseLink` if it's a typo
        if (fullUrl) {
            try {
                imageUrl = encodeURI(new URL(fullUrl).pathname)
            } catch {
                imageUrl = encodeURI(fullUrl)
            }
        }
    }
    return {
        imageUrl,
        pagePath: window.location.pathname || '',
        referrerUrl: typeof document !== 'undefined' ? document.referrer : '',
        inventoryStatus: inventoryStatus(product),
        position: 1,
        searchTerms: querystring
    }
}

/**
 * Returns the inventory status of a product.
 * @param {Object} product - The product object containing inventory data.
 * @returns {string} Inventory status: 'In Stock', 'Out of Stock', 'Backorder', or 'Pre-Order'.
 */
export const inventoryStatus = (product) => {
    if (product?.inventory?.preorderable) {
        return 'Pre-Order'
    } else if (product?.inventory?.backorderable && product?.inventory?.stockLevel <= 0) {
        return 'Backorder'
    } else if (product?.inventory?.ats === 0) {
        return 'Out of Stock'
    } else {
        return 'In Stock'
    }
}

/**
 * Extracts context parameters for a product feed from the DOM.
 * @param {HTMLElement} [currentElement] - DOM element inside the feed container.
 * @returns {Object} Feed context including title, type, paths, and referrer.
 */
export const feedContextprams = (currentElement) => {
    if (!currentElement) {
        return {}
    }

    if (typeof document !== 'undefined' && currentElement) {
        let feedTitle = ''
        let feedType = 'Manual'
        const wrapper = currentElement.closest('.carousel-wrapper')

        if (wrapper) {
            feedTitle =
                wrapper?.querySelector('.recommenderHeading') ??
                wrapper?.parentElement
                    ?.closest('.carousel-wrapper')
                    ?.querySelector('.recommenderHeading')

            feedTitle = feedTitle?.textContent?.replace(/[\n\t]/g, '').trim() || ''
            // fallback if no recommenderHeading
            if (!feedTitle) {
                feedTitle =
                    wrapper
                        .closest('.textTileWithProduct-container')
                        ?.querySelector('.heading')
                        ?.textContent?.replace(/[\n\t]/g, '')
                        ?.trim() || ''
            }

            if (wrapper.classList.contains('ta-wrapper')) {
                feedType = 'ThinkAnalytics'
            } else if (wrapper.classList.contains('einstein')) {
                feedType = 'Einstein'
            }
        }
        return {feedTitle, feedType}
    }
    return {}
}

/**
 * Determines the device type based on screen width.
 * @returns {string} One of: 'Mobile', 'Tablet', or 'Desktop'.
 */
export const getDeviceTypeByScreenSize = () => {
    const width = window && window?.innerWidth
    if (width) {
        if (width <= 768) {
            return 'Mobile'
        } else if (width > 768 && width <= 1024) {
            return 'Tablet'
        } else {
            return 'Desktop'
        }
    } else {
        return 'Desktop'
    }
}

// Function to get products from active slides
export const getActiveSlideProducts = (currentSlide, recommendations, scrollRef, localProducts) => {
    const sliderElement = scrollRef.current?.innerSlider?.list?.querySelector('.slick-track')
    if (!sliderElement) return []
    const activeSlides = sliderElement.querySelectorAll('.slick-slide.slick-active')
    const carouselPageIndex =
        activeSlides.length > 0
            ? parseInt(activeSlides[0].getAttribute('data-index'), 10) || 0
            : currentSlide
    const dataToRender =
        recommendations?.recs ??
        (recommendations?.length > 0 ? recommendations : localProducts ?? [])
    const activeProducts = []
    activeSlides.forEach((slide) => {
        if (!slide.hasAttribute('data-carousel-tracked')) {
            const slideIndex = parseInt(slide.getAttribute('data-index'), 10)
            if (slideIndex >= 0 && slideIndex < dataToRender.length) {
                activeProducts.push(dataToRender[slideIndex])
                slide.setAttribute('data-carousel-tracked', 'true')
            }
        }
    })

    return {
        activeProducts,
        carouselPageIndex,
        carousel: true
    }
}

/**
 * CartViewedDomData function to fetch basket lineItems object
 */
export const getCartViewedDomData = (productsObject, basket) => {
    if (!productsObject || typeof productsObject !== 'object') return []
    const lineItemProducts = Object.values(productsObject).map((item) => {
        const lineItemId = lineItemsUUID(basket, item?.id)
        return {
            category:
                item?.c_category ?? item?.representedProduct?.c_category ?? item?.primaryCategoryId,
            name: item?.name ?? item?.productName,
            price: item?.price,
            sku: item?.id ?? item?.productId,
            url: window.location.href,
            imageUrl: item?.imageGroups?.[0]?.images?.[0]?.disBaseLink,
            productId: lineItemId || (item?.id ?? item?.productId),
            globalName: item?.c_globalName || item?.productName,
            inventoryStatus: inventoryStatus(item)
        }
    })

    return lineItemProducts
}

/**
 * getFilterAppliedDomData function to fetch filter object
 */
export const getFilterAppliedDomData = (productSearchResult, attributeId, value, allfilters) => {
    let filters =
        typeof document !== 'undefined'
            ? Array.from(document.querySelectorAll('.selectedFilters li button')).map((button) => ({
                  filterType: button?.getAttribute('data-refinementname'),
                  filterValue: button?.getAttribute('data-refinementvalue')
              }))
            : []
    let pageTaxonomy = ''

    if (typeof document !== 'undefined') {
        const breadcrumbItems = Array.from(document.querySelectorAll('nav.sf-breadcrumb ol li'))
        const taxonomyArray = breadcrumbItems
            .map((item) => item.textContent.trim())
            .filter((text) => !!text)
        pageTaxonomy = taxonomyArray.length > 0 ? taxonomyArray.join('/') : ''
    }

    if (allfilters && value) {
        const recentFilter = allfilters.find((f) => f.attributeId === attributeId)
        if (recentFilter) {
            filters.push({
                filterType: recentFilter.label,
                filterValue: value.label
            })
        }
    }

    const pagePath = typeof window !== 'undefined' ? window.location.pathname : ''
    const pageId = typeof window !== 'undefined' ? window.location.origin + pagePath : ''
    const feedType = productSearchResult?.query ? 'Search' : 'Category'
    const listid = productSearchResult?.cgid || ''
    const category = productSearchResult?.cgid || ''
    const feedTitle = productSearchResult?.cgid || productSearchResult?.query || ''

    return {
        feedTitle,
        category,
        listid,
        filters,
        pageTaxonomy,
        pageId,
        feedType
    }
}
